import { graphql } from 'react-relay';
import React, { useContext, useCallback, useRef } from 'react';
import type { MutationUpdaterFn } from 'apollo-client';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	reopenCommentInDataMap,
	resolveCommentInDataMap,
	updateCommentsQueryCacheForResolve,
} from '@confluence/comments-data/entry-points/commentsDataHelper.ts';
import {
	useCommentsData,
	useCommentsDataActions,
	CommentActionType,
	CommentType,
	type CommentData,
	type ReplyData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useUpdateCommentInCache } from '@confluence/comments-data-manager/entry-points/useUpdateCommentInCache.ts';
import {
	selectUpdateFunction,
	resolveCommentInCacheCallback,
	reopenCommentInCacheCallback,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment.ts';
import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody.ts';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import {
	handleResolveSuccess,
	handleMutationFailure,
	parseError,
	getTranslatedError,
	isAlreadyDeletedError,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import {
	hasContributors,
	type CommentAction,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { usePageContentId } from '@confluence/page-context';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation.ts';
import { InlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.graphql.ts';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper.ts';
import type { CommentFooterLocation } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import type {
	TopLevelComment,
	GeneralCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	DELETE_PAGE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { i18n as inlineCommentsI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import {
	updateApolloCacheParentCallback as updateApolloCacheGeneralParentCallback,
	updateApolloCacheReplyCallback as updateApolloCacheGeneralReplyCallback,
} from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getLogger } from '@confluence/logger';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import {
	deleteParentCommentOptimistically,
	deleteReplyCommentOptimistically,
} from '@confluence/comments-panel/entry-points/commentHelpers.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { fg } from '@confluence/feature-gating';
import { AvatarWithActionButtonAsync } from '@confluence/agent-mention/entry-points/AvatarWithActionButtonAsync.ts';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import { useCommentAgentSession } from '@confluence/agent-mention/entry-points/useCommentAgentSession.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	ObjectCommentDeleteGeneralCommentMutation as ObjectCommentDeleteGeneralCommentMutation$data,
	ObjectCommentDeleteGeneralCommentMutationVariables as ObjectCommentDeleteGeneralCommentMutation$variables,
} from './__types__/ObjectCommentDeleteGeneralCommentMutation.ts';
import type {
	ObjectCommentReopenCommentMutation as ObjectCommentReopenCommentMutation$data,
	ObjectCommentReopenCommentMutationVariables as ObjectCommentReopenCommentMutation$variables,
} from './__types__/ObjectCommentReopenCommentMutation.ts';
import type {
	ObjectCommentResolveCommentMutation as ObjectCommentResolveCommentMutation$data,
	ObjectCommentResolveCommentMutationVariables as ObjectCommentResolveCommentMutation$variables,
} from './__types__/ObjectCommentResolveCommentMutation.ts';
const ObjectCommentResolveCommentMutation = graphql`
	mutation ObjectCommentResolveCommentMutation($commentIds: [ID]!, $cloudId: ID!) {
		confluence_resolveComments(commentIds: $commentIds, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				resolveProperties {
					resolvedTime
					resolvedUser
					resolvedFriendlyDate
				}
				status
			}
			errors {
				message
				extensions {
					... on ResolveCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const ObjectCommentReopenCommentMutation = graphql`
	mutation ObjectCommentReopenCommentMutation($commentId: ID!, $cloudId: ID!) {
		confluence_reopenComment(commentId: $commentId, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				status
			}
			errors {
				message
				extensions {
					... on ReopenCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const ObjectCommentDeleteGeneralCommentMutation = graphql`
	mutation ObjectCommentDeleteGeneralCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
type CommentProps = {
	comment: CommentData | ReplyData;
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	pageInfo: PageInfoNode | null;
	isUnread?: boolean;
	isHovered?: boolean;
	threadKey: string;
	flags?: FlagsStateContainer;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isThreadHovered?: boolean;
	isParentCommentOpen?: boolean;
	adjustForFooterNestedReplies?: boolean;
	topLevelParentCommentId?: string;
	commentHasChildren?: boolean;
	isLastNestedFooterReply?: boolean;
	isOffline?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	replyHasNestedObjects?: boolean;
};

const i18n = defineMessages({
	commentReopenedFlagTitle: {
		id: 'object-comments.comment.reopened.flag.title',
		defaultMessage: 'Comment reopened',
		description:
			'Title to display in Flag to inform user when the comment was successfully reopened.',
	},
	commentReopenFailedFlagTitle: {
		id: 'object-comments.comment.reopen.failed.flag.title',
		defaultMessage: 'Failed to reopen comment',
		description: 'Title to display in Flag to inform user when the comment failed to reopen.',
	},
	commentReopenCacheUpdateFailedFlagTitle: {
		id: 'object-comments.comment.reopen.cache-update.failed.flag.title',
		defaultMessage: 'Failed to update reopened comment. Please refresh the page.',
		description:
			'Title to display in Flag to inform user when the comment was reopened but the local cache update failed.',
	},
	retryCTA: {
		id: 'object-comments.comment.reopen.failed.flag.cta.retry',
		defaultMessage: 'Retry now',
		description:
			'CTA button text displayed in Error Flag for users to retry the failed comment reopen action.',
	},
});

const logger = getLogger('page-comments-queries');

// this component can be used for parent comment and a reply
export const ObjectComment = ({
	comment,
	supportedTopLevelActions,
	pageMode,
	pageInfo,
	isUnread = false,
	isHovered,
	threadKey,
	flags,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	canAddComments = true,
	isThreadHovered,
	isParentCommentOpen,
	adjustForFooterNestedReplies = false,
	topLevelParentCommentId,
	commentHasChildren = false,
	isLastNestedFooterReply = false,
	isOffline = false,
	showRepliesWithCurvedBorders,
	replyHasNestedObjects = false,
}: CommentProps): React.JSX.Element => {
	// Store stable references to break circular dependency in useCallback
	const handleResolveCommentRef = useRef<(() => void) | null>(null);
	const handleReopenCommentRef = useRef<(() => void) | null>(null);
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { resetContentChanged } = useCommentsContentActions();
	const [contentId = ''] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [
		{
			commentsDataMap: legacyCommentsDataMap,
			hasNestedComments: legacyHasNestedComments,
			cacheUpdateFailed,
		},
		{ addNewCommentThreads, addReplyToCommentThread, deleteParentComment, handleRemovingComments },
	] = useCommentsData();
	const { setCacheUpdateFailed } = useCommentsDataActions();

	// Get cache operations for new comments data manager
	const { deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	// Use new or legacy data based on experiment
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? processedCommentsData.commentsDataMap
		: legacyCommentsDataMap;

	// Use new or legacy hasNestedComments based on experiment
	const hasNestedComments = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? processedCommentsData.hasNestedComments
		: legacyHasNestedComments;

	const { cloudId } = useSessionData();

	const [
		{ currentlySelectedThreadKey, footerEditCommentIds },
		{
			setCurrentView,
			setCurrentlySelectedThreadKey,
			setReplyToCommentId,
			addFooterEditCommentId,
			removeFooterEditCommentId,
		},
	] = useCommentsPanel();

	const { formatMessage } = useIntl();
	const { focusedCommentId } = useInlineCommentQueryParams();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();

	const allowComments = useEmbeddedComments();
	const [resolveCommentFn] = useMutationInterop<
		ObjectCommentResolveCommentMutation$data,
		ObjectCommentResolveCommentMutation$variables
	>(ObjectCommentResolveCommentMutation, buildMutationContextForNoNetworkError());
	const resolveCommentMutationFn = resolveCommentFn;
	const [reopenCommentFn] = useMutationInterop<
		ObjectCommentReopenCommentMutation$data,
		ObjectCommentReopenCommentMutation$variables
	>(ObjectCommentReopenCommentMutation, {
		refetchQueries:
			// Copied from the existing dialog flow to support standalone experience
			pageMode === PageMode.VIEW &&
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? [{ query: InlineCommentsQuery, variables: { pageId: contentId } }]
				: [],
		...buildMutationContextForNoNetworkError(),
	});

	// Get the update functions for the comments data manager - aliased to support the different
	// mutation's different result types
	const { updateCommentInCache: updateResolveCommentInCache } =
		useUpdateCommentInCache<ObjectCommentResolveCommentMutation$data>();
	const { updateCommentInCache: updateReopenCommentInCache } =
		useUpdateCommentInCache<ObjectCommentReopenCommentMutation$data>();
	const [deleteGeneralCommentFn] = useMutationInterop<
		ObjectCommentDeleteGeneralCommentMutation$data,
		ObjectCommentDeleteGeneralCommentMutation$variables
	>(ObjectCommentDeleteGeneralCommentMutation, buildMutationContextForNoNetworkError());

	const userId = (comment?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = comment?.author?.profilePicture?.path;
	const topCommentDisplayName =
		comment?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = comment?.id;
	const date = comment?.version?.when;
	const createdAt = comment?.createdAt?.value;
	const createdAtNonLocalized = comment?.createdAtNonLocalized;
	const versionByDisplayName = comment?.version?.by?.displayName;
	const versionDate = comment?.version?.when;
	const permissions = comment?.permissions;
	const content = comment?.body?.value;
	const permissionType = comment?.author?.permissionType ?? undefined;
	const parentCommentId = comment?.parentId;
	const isReply = !!parentCommentId;
	const isEdited = (comment?.version?.number || 0) > 1;
	const numReplies = (comment as TopLevelComment)?.replies?.length || 0;
	const commentUrl = comment?.links.webui ?? undefined;
	const inheritedReactionsData = comment?.reactionsSummary;

	const footerCommentLocation = comment?.location as CommentFooterLocation;

	const getResolvedProperties = useCallback(() => {
		const properties = footerCommentLocation?.commentResolveProperties;
		if (!isReply) {
			return properties || undefined;
		}
		return undefined;
	}, [footerCommentLocation, isReply]);

	const resolveProperties = getResolvedProperties();

	const isCommentRemovedByAnotherUser = comment?.wasRemovedByAnotherUser;

	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;
	const isDeletedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_PAGE_COMMENT_EXPERIENCE,
						error: err,
						commentId: removalCommentId,
					});
				},
			}),
		[deleteCommentInCache, experienceTracker],
	);

	// This used to be behind confluence_frontend_content_wrapper, but that gate is rolled out.
	// Keeping this as a single variable keeps experiment exposures constant, and the `|| false`
	// avoids feature-gating ratcheting concerns until confluence_fe_delete_comment_optimistic is cleaned up.
	const isOptimisticDeleteEnabled =
		expVal('confluence_fe_delete_comment_optimistic', 'isEnabled', false) || false;

	const nonMemoizedHandleDeleteGeneralComment = () => {
		let deleteLocation: CommentDeletionLocation | undefined;

		const deleteVariables = {
			variables: {
				commentIdToDelete: commentId,
				deleteFrom: deleteLocation,
			},
			update: selectUpdateFunction<ObjectCommentDeleteGeneralCommentMutation$data>(
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
				() => deleteCommentInCacheOnUpdate(commentId),
				() =>
					isReply
						? (updateApolloCacheGeneralReplyCallback(
								'delete',
								{
									pageId: contentId,
									contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
									type: ['FOOTER'],
								},
								parentCommentId,
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<ObjectCommentDeleteGeneralCommentMutation$data>)
						: (updateApolloCacheGeneralParentCallback(
								'delete',
								{
									pageId: contentId,
									contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
									type: ['FOOTER'],
								},
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<ObjectCommentDeleteGeneralCommentMutation$data>),
			),
			optimisticResponse: isOptimisticDeleteEnabled ? { deleteComment: true } : undefined,
		};

		// Optimistically delete the comment thread, if it is not a reply.
		const deletedParentComment =
			!isReply &&
			isOptimisticDeleteEnabled &&
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? deleteParentCommentOptimistically({
						threadKey,
						commentType: CommentType.GENERAL,
						commentsDataMap,
						deleteParentComment,
					})
				: null;
		// Optimistically delete the reply, if it is a reply.
		const deletedReplyComment =
			isReply &&
			isOptimisticDeleteEnabled &&
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? deleteReplyCommentOptimistically({
						threadKey,
						commentId,
						commentType: CommentType.GENERAL,
						commentsDataMap,
						handleRemovingComments,
					})
				: null;

		deleteGeneralCommentFn({ ...deleteVariables })
			.then(({ data }) => {
				if (!data || !data.deleteComment) {
					throw new Error('No data returned from mutation');
				}

				resetContentChanged();

				if (
					!isOptimisticDeleteEnabled &&
					!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				) {
					if (isReply) {
						handleRemovingComments({
							threadKey,
							commentId,
							action: CommentActionType.DELETE_COMMENT,
							commentType: CommentType.GENERAL,
						});
					} else {
						deleteParentComment({
							threadKey,
							commentType: CommentType.GENERAL,
						});
					}
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'deleted',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: 'page',
						objectId: contentId,
						source: AnalyticsSource.PAGE_FOOTER,
						attributes: {
							...getCommentsDataManagerExperimentAttributes(),
							commentType: AnalyticsCommentType.PAGE,
							source: AnalyticsSource.PAGE_FOOTER,
							pageMode,
							isReply,
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: DELETE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				const parsedError = parseError(err);
				const translatedError = getTranslatedError(parsedError.message, commentId);

				if (isAlreadyDeletedError(translatedError)) {
					void flags?.showWarningFlag({
						title: <FormattedMessage {...inlineCommentsI18n.commentAlreadyDeletedTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'alreadyDeleted',
							actionSubject: 'comment',
							actionSubjectId: commentId,
							objectType: 'page',
							objectId: contentId,
							source: AnalyticsSource.PAGE_FOOTER,
							attributes: {
								...getCommentsDataManagerExperimentAttributes(),
								commentType: CommentType.GENERAL,
								source: AnalyticsSource.PAGE_FOOTER,
								mode: pageMode,
								isReply,
							},
						},
					}).fire();
				} else if (isOptimisticDeleteEnabled) {
					// show an error flag to the user when optimistic deletion fails
					void flags?.showErrorFlag({
						id: commentId,
						title: <FormattedMessage {...inlineCommentsI18n.deleteErrorTitle} />,
						description: <FormattedMessage {...inlineCommentsI18n.deleteError} />,
					});
				}

				// add back the optimistically deleted comment or reply
				if (
					deletedParentComment &&
					isOptimisticDeleteEnabled &&
					!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				) {
					addNewCommentThreads(deletedParentComment as LegacyCommentsDataMap);
				}
				if (
					deletedReplyComment &&
					isOptimisticDeleteEnabled &&
					!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				) {
					addReplyToCommentThread(
						threadKey,
						deletedReplyComment as ReplyData,
						CommentType.GENERAL,
						true,
					);
				}

				void handleMutationFailure({
					experienceTracker,
					experienceName: DELETE_PAGE_COMMENT_EXPERIENCE,
					error: err,
					commentId,
				});
			});
	};

	const memoizedHandleDeleteGeneralComment = useCallback(nonMemoizedHandleDeleteGeneralComment, [
		commentId,
		createAnalyticsEvent,
		deleteGeneralCommentFn,
		deleteParentComment,
		deleteCommentInCacheOnUpdate,
		experienceTracker,
		flags,
		handleRemovingComments,
		isReactionsEnabled,
		isReply,
		contentId,
		pageMode,
		parentCommentId,
		resetContentChanged,
		threadKey,
		isOptimisticDeleteEnabled,
		addNewCommentThreads,
		addReplyToCommentThread,
		commentsDataMap,
	]);

	/**
	 * editComment, deleteComment, and resolveComment cause instability in CommentBody when rendering. Although
	 * useStableCallback would be more ideal to simplify the overhead for consumers of CommentBody, these functions
	 * as props are used as proxies for permissions meaning they could be undefined which violates the typing for useStableCallback
	 */
	const handleDeleteGeneralComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleDeleteGeneralComment
		: nonMemoizedHandleDeleteGeneralComment;

	const nonMemoHandleEditComment = () => {
		addFooterEditCommentId(commentId);
	};

	const memoizedHandleEditComment = useCallback(nonMemoHandleEditComment, [
		addFooterEditCommentId,
		commentId,
	]);

	const handleEditComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleEditComment
		: nonMemoHandleEditComment;

	const exitCommentEditor = () => {
		removeFooterEditCommentId(commentId);
	};

	const handleReplyToComment = (id: string) => {
		setReplyToCommentId({
			// If we allow nested comments, reply directly to the comment (set in CommentActions)
			// Otherwise, reply to the to the parent comment
			parentCommentId: hasNestedComments ? id : topLevelParentCommentId || id,
			location: LocationValue.FOOTER,
			user: {
				// This field controls who we auto mention in the reply
				displayName: comment?.author?.displayName,
				accountId: (comment?.author as InlineCommentAuthorKnownUser)?.accountId,
			},
		});
	};

	const handleReopenMutationFailure = useCallback(
		({ isCacheUpdateFailed }: { isCacheUpdateFailed: boolean }) => {
			// TODO: Add experience tracking for reopening, which will allow us to differentiate and
			// detect cache errors vs mutation errors by the error value in the stopOnError() experience
			// call.  When that is added, we can go back to having a single flag message.
			void flags?.showErrorFlag({
				id: commentId,
				title: formatMessage(
					isCacheUpdateFailed
						? i18n.commentReopenCacheUpdateFailedFlagTitle
						: i18n.commentReopenFailedFlagTitle,
				),
				isAutoDismiss: true,
				actions: [
					{
						content: formatMessage(i18n.retryCTA),
						onClick: () => {
							void flags.hideFlag(flags.state.flags[0]?.id);
							handleReopenCommentRef.current?.();
						},
					},
				],
			});
		},
		[commentId, flags, formatMessage],
	);

	const reopenCommentUpdateFn: MutationUpdaterFn<ObjectCommentReopenCommentMutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateReopenCommentInCache({
					mutationResult,
					commentId,
					updateCallback: reopenCommentInCacheCallback,
					onError: () => handleReopenMutationFailure({ isCacheUpdateFailed: true }),
				}),
			[commentId, handleReopenMutationFailure, updateReopenCommentInCache],
		);

	const nonMemoHandleReopenComment = () => {
		// NOTE: The reopen experience is started in CommentActions.tsx
		reopenCommentFn({
			variables: { commentId, cloudId },
			update: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? reopenCommentUpdateFn
				: undefined,
		})
			.then(() => {
				// Update the state for panel experience
				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
						reopenCommentInDataMap(
							commentsDataMap as LegacyCommentsDataMap,
							CommentType.GENERAL,
							threadKey,
							addNewCommentThreads,
						);
					} else {
						const commentsDataMapClone = { ...commentsDataMap } as LegacyCommentsDataMap;
						const commentInMap = commentsDataMapClone[CommentType.GENERAL][threadKey];
						if (commentInMap) {
							commentInMap.isOpen = true;
							commentInMap.wasRemovedByAnotherUser = false;

							addNewCommentThreads(commentsDataMapClone);

							const commentLocation = commentInMap.location;

							if (
								commentLocation &&
								'commentResolveProperties' in commentLocation &&
								commentLocation.commentResolveProperties
							) {
								(commentLocation.commentResolveProperties as any).resolved = false;
							}
						}
					}
				}

				// Show the reopen flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(i18n.commentReopenedFlagTitle),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(inlineCommentsI18n.goToCommentCTA),
							onClick: () => {
								if (!isCommentsPanelShown) {
									showCommentsPanel({ openWithView: ViewValues.OPEN });
								} else {
									setCurrentView(ViewValues.OPEN);
								}

								if (expValEquals('confluence_comments_scrollto_resolve', 'isEnabled', true)) {
									void handleCommentsPanelNavigationWithViewChange({
										scrollBehavior: ScrollBehavior.SMOOTH,
										context: NavigationContext.PANEL,
										block: 'center',
										annotationId: threadKey,
										commentType: 'FOOTER',
										annotationClicked: true,
										expectedView: ViewValues.OPEN,
										commentId,
									});
								}

								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(inlineCommentsI18n.undoCTA),
							onClick: () => {
								handleResolveCommentRef.current?.();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});
			})
			.catch(() => {
				handleReopenMutationFailure({ isCacheUpdateFailed: false });
			});
	};

	const memoHandleReopenComment = useCallback(nonMemoHandleReopenComment, [
		addNewCommentThreads,
		cloudId,
		commentId,
		commentsDataMap,
		flags,
		formatMessage,
		handleReopenMutationFailure,
		isCommentsPanelShown,
		reopenCommentFn,
		reopenCommentUpdateFn,
		setCurrentView,
		showCommentsPanel,
		threadKey,
	]);

	const handleResolveMutationFailure = useCallback(
		(resolveError: Error) => {
			void handleMutationFailure({
				experienceTracker,
				experienceName: RESOLVE_PAGE_COMMENT_EXPERIENCE,
				error: resolveError,
			});
		},
		[experienceTracker],
	);

	const resolveCommentsUpdateFn: MutationUpdaterFn<ObjectCommentResolveCommentMutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateResolveCommentInCache({
					mutationResult,
					commentId,
					updateCallback: resolveCommentInCacheCallback,
					onError: handleResolveMutationFailure,
				}),
			[updateResolveCommentInCache, commentId, handleResolveMutationFailure],
		);

	const nonMemoHandleResolveComment = () => {
		if (
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
			expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
		) {
			// Optimistically mark the comment as resolved in the data map & update UI early
			resolveCommentInDataMap(
				commentsDataMap as LegacyCommentsDataMap,
				CommentType.GENERAL,
				threadKey,
				addNewCommentThreads,
			);
		}

		const mutationVariables = { commentIds: [commentId], cloudId };
		const query = ActiveCommentsQuery;

		const queryVariables = buildConsistentActiveCommentsQueryVariables({
			pageId: contentId,
			type: 'FOOTER', // If we have footer, always write to footer. Panel works either way
		});

		// NOTE: The RESOLVED experience is started in CommentActions.tsx
		resolveCommentMutationFn({
			variables: mutationVariables,
			update: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? resolveCommentsUpdateFn
				: (cache) => {
						updateCommentsQueryCacheForResolve({
							cache,
							commentId,
							query,
							queryVariables,
							cacheUpdateFailed,
							setCacheUpdateFailed,
							source: AnalyticsSource.PAGE_FOOTER,
							logger,
						});
					},
			// When a `optimisticResponse` is set, useMutation invokes the update function twice:
			// first immediately, and after mutation succeeds, a second time. If mutation fails,
			// the cache updated by the update function is rolled back. Thus, this optimistic
			// resolve works both when cc_comments_new_comments_data_manager is ON and OFF.
			optimisticResponse: expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
				? ({
						confluence_resolveComments: {
							commentResolutionStates: [
								{
									commentId,
									status: true,
									resolveProperties: {
										// These 2 values will be updated to the correct value after mutation succeeds.
										// - `resolvedUser` is only displayed in comments panel Resolved view,
										//   inside a tooltip when hovered.
										// - `resolvedFriendlyDate` is part of the mutation payload, but the current UI
										//   displays `resolvedTime`.
										// So setting it as empty optimistically won't affect the static UI.
										resolvedUser: null,
										resolvedFriendlyDate: '',
										resolvedTime: Date.now(),
										__typename: 'InlineCommentResolveProperties',
									},
									__typename: 'ConfluenceCommentResolutionState',
								},
							],
							success: true,
							errors: [],
							__typename: 'ConfluenceResolveCommentsPayload',
						},
					} as unknown as ObjectCommentResolveCommentMutation$data)
				: undefined,
		})
			.then(() => {
				if (
					!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
					!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
				) {
					// Update the state for panel experience when optimistic resolve is disabled
					// If enabled, this has been done before the mutation succeeds.
					if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
						resolveCommentInDataMap(
							commentsDataMap as LegacyCommentsDataMap,
							CommentType.GENERAL,
							threadKey,
							addNewCommentThreads,
						);
					} else {
						const commentsDataMapClone = { ...commentsDataMap } as LegacyCommentsDataMap;
						const commentInMap = commentsDataMapClone[CommentType.GENERAL][threadKey];

						if (commentInMap) {
							commentsDataMapClone[CommentType.GENERAL][threadKey].isOpen = false;
							const commentLocation = commentsDataMapClone[CommentType.GENERAL][threadKey]
								.location as GeneralCommentLocation;
							if (commentLocation && commentLocation.commentResolveProperties) {
								(commentLocation.commentResolveProperties as any).resolved = true;
							}
							addNewCommentThreads(commentsDataMapClone);
						}
					}
				}

				handleResolveSuccess({
					commentId,
					parentCommentMarkerRef: threadKey,
					pageId: contentId,
					pageMode,
					source: AnalyticsSource.PAGE_FOOTER,
					commentType: CommentType.GENERAL,
					createAnalyticsEvent,
					//getInlineNodeTypes  // TODO: Figure out how to get this info
				});

				if (allowComments) {
					void flags?.showSuccessCircleFlag({
						id: commentId,
						title: formatMessage(inlineCommentsI18n.resolvedFlagText),
						isAutoDismiss: true,
						actions: [
							{
								content: formatMessage(inlineCommentsI18n.goToCommentCTA),
								onClick: () => {
									if (!isCommentsPanelShown) {
										showCommentsPanel({ openWithView: ViewValues.RESOLVED });
									} else {
										setCurrentView(ViewValues.RESOLVED);
									}

									if (expValEquals('confluence_comments_scrollto_resolve', 'isEnabled', true)) {
										void handleCommentsPanelNavigationWithViewChange({
											scrollBehavior: ScrollBehavior.SMOOTH,
											context: NavigationContext.PANEL,
											block: 'center',
											annotationId: threadKey,
											commentType: 'FOOTER',
											annotationClicked: true,
											expectedView: ViewValues.RESOLVED,
											commentId,
										});
									}

									void flags.hideFlag(flags.state.flags[0]?.id);
								},
							},
							{
								content: formatMessage(inlineCommentsI18n.undoCTA),
								onClick: () => {
									handleReopenCommentRef.current?.();
									void flags.hideFlag(flags.state.flags[0]?.id);
								},
							},
						],
					});
				}

				experienceTracker.succeed({
					name: RESOLVE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
					if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						// Undo resolving the comment when optimistic resolve is enabled
						reopenCommentInDataMap(
							commentsDataMap as LegacyCommentsDataMap,
							CommentType.GENERAL,
							threadKey,
							addNewCommentThreads,
						);
					}

					// Show an error flag to user when resolve fails
					void flags?.showErrorFlag({
						id: commentId,
						title: formatMessage(inlineCommentsI18n.resolveErrorTitle),
						description: formatMessage(inlineCommentsI18n.resolveError),
					});
				}

				handleResolveMutationFailure(err);
			});
	};

	const memoizedHandleResolveComment = useCallback(nonMemoHandleResolveComment, [
		addNewCommentThreads,
		allowComments,
		cacheUpdateFailed,
		cloudId,
		commentId,
		commentsDataMap,
		createAnalyticsEvent,
		experienceTracker,
		flags,
		formatMessage,
		handleResolveMutationFailure,
		isCommentsPanelShown,
		contentId,
		pageMode,
		resolveCommentMutationFn,
		resolveCommentsUpdateFn,
		setCacheUpdateFailed,
		setCurrentView,
		showCommentsPanel,
		threadKey,
	]);

	const handleResolveComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleResolveComment
		: nonMemoHandleResolveComment;

	const handleReopenComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoHandleReopenComment
		: nonMemoHandleReopenComment;

	// Update refs to break circular dependency
	handleResolveCommentRef.current = handleResolveComment;
	handleReopenCommentRef.current = handleReopenComment;

	const pageType = pageInfo?.type ?? '';

	// Agent session — wire SSE state for the agent-mention-in-comment feature.
	// Any comment (top-level OR reply) can be associated with an agent session, so
	// each comment looks up its OWN session by commentId from AgentSessionsContext
	// (the map is keyed by the numeric comment id parsed from the session's
	// subEntityId ARI, populated by the page-level fetch). When there is no session
	// the hook is a no-op. The experiment is inlined per
	// confluence-feature-gating/inline-usage and ordered last (gate-last).
	const agentSessions = useAgentSessionsContext();
	const agentSession = commentId ? agentSessions.get(commentId) : undefined;
	const { state: agentSessionState } = useCommentAgentSession(
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ? (commentId ?? '') : '',
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? agentSession?.conversationId
			: undefined,
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? (agentSession?.state ?? undefined)
			: undefined,
	);

	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	// User space permissions - only access when feature gate is on
	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	// We only want to focus resolved comments because the 'active' comments have
	// their own treatment for when one is hovered/selected and could cause weird behavior
	const isCommentFocused = resolveProperties?.resolved && commentId === focusedCommentId;

	const supportedActions =
		!isReply && isCommentRemovedByAnotherUser
			? (['reopen'] as CommentAction[])
			: supportedTopLevelActions;

	const commentContent = footerEditCommentIds?.has(commentId) ? (
		<EditComment
			pageId={contentId}
			date={date ?? ''}
			createdAtNonLocalized={createdAtNonLocalized}
			pageType={pageType}
			commentId={commentId}
			annotationId={threadKey}
			spaceId={spaceId}
			content={content ?? ''}
			isReply={isReply}
			displayCommentInViewMode={exitCommentEditor}
			avatarUrl={topCommentUserAvatar ?? ''}
			displayName={topCommentDisplayName}
			dateUrl={commentUrl ?? ''}
			versionByDisplayName={versionByDisplayName ?? ''}
			versionDate={versionDate ?? ''}
			mode={pageMode === PageMode.VIEW ? 'view' : 'edit'}
			hasMediaUploadPermissions={hasMediaUploadPermissions}
			isFooterComment
			commentType={CommentType.GENERAL}
			adjustForFooterReplies={
				expValEquals('confluence_comments_tuneup', 'isEnabled', true)
					? commentHasChildren || (!isReply && numReplies > 0)
					: commentHasChildren || !isReply
			}
			isLastNestedFooterReply={isLastNestedFooterReply}
			isOffline={isOffline}
		/>
	) : (
		<CommentBody
			mode="view"
			annotationId={threadKey}
			userId={userId}
			avatarUrl={topCommentUserAvatar}
			displayName={topCommentDisplayName}
			date={date ?? ''}
			dateUrl={commentUrl ?? ''}
			createdAt={createdAt ?? ''}
			createdAtNonLocalized={createdAtNonLocalized ?? ''}
			versionByDisplayName={versionByDisplayName ?? ''}
			versionDate={versionDate ?? ''}
			pageId={contentId}
			pageType={pageType}
			commentId={commentId ?? ''}
			isReply={isReply}
			isEdited={isEdited}
			parentCommentId={parentCommentId}
			permissions={permissions}
			content={content ?? ''}
			permissionType={(permissionType as any) ?? undefined}
			supportedActions={supportedActions}
			deleteComment={handleDeleteGeneralComment}
			editComment={handleEditComment}
			resolveComment={handleResolveComment}
			numReplies={numReplies}
			isUnread={isUnread}
			isCommentActive
			isCommentsPanel
			isHovered={isHovered}
			isResolvedByAnotherUser={isResolvedByAnotherUser}
			isFocused={isCommentFocused}
			resolveProperties={resolveProperties}
			replyToComment={handleReplyToComment}
			inheritedReactionsData={inheritedReactionsData}
			isDeletedByAnotherUser={isDeletedByAnotherUser}
			currentlySelectedThreadKey={currentlySelectedThreadKey}
			setCurrentlySelectedThreadKey={setCurrentlySelectedThreadKey}
			commentType={CommentType.GENERAL}
			adjustForNestedReplies={adjustForNestedReplies}
			hideBranchingStyle={hideBranchingStyle}
			canAddComments={canAddComments}
			isThreadHovered={isThreadHovered}
			isParentCommentOpen={isParentCommentOpen}
			isFooterComment
			adjustForFooterNestedReplies={adjustForFooterNestedReplies}
			isOffline={isOffline}
			showRepliesWithCurvedBorders={showRepliesWithCurvedBorders}
			replyHasNestedObjects={replyHasNestedObjects}
			contributors={hasContributors(comment) ? comment.contributors : undefined}
		/>
	);

	// When this comment (top-level or reply) has an associated agent session, attach
	// the AvatarWithActionButton (from AFM PR #370723) BELOW the comment — the comment
	// still renders normally. The component drives its label/action off agentState.
	// Interim — prop values will be refined once the design branch is fully merged.
	// Gate inlined and ordered last per confluence-feature-gating rules.
	return (
		<>
			{commentContent}
			{agentSessionState &&
				expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) &&
				(isReply ? (
					// Nested replies: wrap the status row in the same curved branch a reply uses so it
					// attaches to the thread's connector line. Top-level comments render it unwrapped.
					<WithCurvedBranchingStyle
						showDefaultStyles
						customStyles={
							expValEquals('confluence_comments_tuneup', 'isEnabled', true)
								? { left: '-16px', height: '24px' }
								: { height: '25px' }
						}
					>
						<AvatarWithActionButtonAsync
							agentState={agentSessionState}
							conversationId={agentSession?.conversationId ?? ''}
							isCommentsPanel={isCommentsPanelShown}
							isFooterComment
							userId={agentSession?.agentId ?? userId}
							avatarUrl={agentSession?.avatarUrl ?? topCommentUserAvatar}
							agentName={agentSession?.agentName ?? undefined}
							isReply
						/>
					</WithCurvedBranchingStyle>
				) : (
					<AvatarWithActionButtonAsync
						agentState={agentSessionState}
						conversationId={agentSession?.conversationId ?? ''}
						isCommentsPanel={isCommentsPanelShown}
						isFooterComment
						userId={agentSession?.agentId ?? userId}
						avatarUrl={agentSession?.avatarUrl ?? topCommentUserAvatar}
						agentName={agentSession?.agentName ?? undefined}
					/>
				))}
		</>
	);
};
